<template>
    <div
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <show-all-new
            :title="pageTitle"
            :items="getShowAllMoviesCards"
            :is-loading="getIsLoadingShowAll"
            @item-selected="itemSelected"
            @request-more="fetchMoreItems"
        />
    </div>
</template>

<script lang="ts" setup>
    import { type ComputedRef, computed} from "vue";
    import type { OnDemandCardData } from "@/components/Cards/types";
    import { Strings } from "@/locales/types/enums";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { useRouter } from 'vue-router'
    import { useI18n } from "vue-i18n";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useLinesVodGenresStore } from "@/stores/LinesVodGenres";
    import { storeToRefs } from 'pinia';
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { useApplicationStore } from "@/stores/Application";

    const VodPageStore = useLinesVodGenresStore();
    const {
        loadShowAllMoviesNextPage,
    } = VodPageStore;
    const {
        getShowAllMoviesCards,
        getIsLoadingShowAll,
    } = storeToRefs(VodPageStore);

    const ApplicationStore = useApplicationStore();
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const { 
        setViewedDetailItems,
    } = useDetailPageStore();

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-vod-show-all-movies'],
    });

    const { $navigator } = useNuxtApp();
    const router = useRouter();
    const { t } = useI18n();

    const pageTitle: ComputedRef<string> = computed(() => t(Strings.VOD_MOVIES).toString());

    const fetchMoreItems = async (): Promise<void> => {
        await loadShowAllMoviesNextPage();
    };
    const itemSelected = (event: IEventSelected): void => {
        try {
            const item = event.item as OnDemandCardData;
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(event.item));
            }

            setViewedDetailItems(playItem);
            router.push(`${linkItems.detailsVodMovies.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
        }
    };

    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };
</script>
